/**
 * Registration Session Management
 * Manages temporary session data during multi-step registration
 * Uses sessionStorage for security (data cleared when browser closes)
 */

const SESSION_KEY = 'registration_session'
const SESSION_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes

export interface RegistrationSessionData {
  phoneNumber: string
  otpVerified: boolean
  password: string
  sessionToken: string
  agreeTerms: boolean
  agreePrivacy: boolean
  createdAt: number
  expiresAt: number
}

/**
 * Save Step 1 data (OTP + Password verification)
 */
export function saveStep1Data(data: {
  phoneNumber: string
  password: string
  sessionToken: string
  agreeTerms: boolean
  agreePrivacy: boolean
}): void {
  const now = Date.now()
  const sessionData: RegistrationSessionData = {
    phoneNumber: data.phoneNumber,
    otpVerified: true,
    password: data.password,
    sessionToken: data.sessionToken,
    agreeTerms: data.agreeTerms,
    agreePrivacy: data.agreePrivacy,
    createdAt: now,
    expiresAt: now + SESSION_TIMEOUT_MS,
  }

  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData))
  } catch (error) {
    console.error('Failed to save registration session:', error)
    throw new Error('ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง')
  }
}

/**
 * Get Step 1 data with validation
 * Returns null if session is invalid or expired
 */
export function getStep1Data(): RegistrationSessionData | null {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (!stored) {
      return null
    }

    const sessionData: RegistrationSessionData = JSON.parse(stored)
    
    // Check if session has expired
    if (Date.now() > sessionData.expiresAt) {
      clearRegistrationSession()
      return null
    }

    // Validate required fields
    if (
      !sessionData.phoneNumber ||
      !sessionData.password ||
      !sessionData.sessionToken ||
      !sessionData.otpVerified
    ) {
      clearRegistrationSession()
      return null
    }

    return sessionData
  } catch (error) {
    console.error('Failed to read registration session:', error)
    clearRegistrationSession()
    return null
  }
}

/**
 * Check if session is valid
 */
export function isSessionValid(): boolean {
  const sessionData = getStep1Data()
  return sessionData !== null
}

/**
 * Get remaining session time in seconds
 */
export function getSessionTimeRemaining(): number {
  const sessionData = getStep1Data()
  if (!sessionData) return 0

  const remaining = Math.max(0, sessionData.expiresAt - Date.now())
  return Math.floor(remaining / 1000)
}

/**
 * Clear registration session
 */
export function clearRegistrationSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch (error) {
    console.error('Failed to clear registration session:', error)
  }
}

/**
 * Extend session timeout (refresh expiration)
 */
export function extendSession(): boolean {
  const sessionData = getStep1Data()
  if (!sessionData) return false

  const now = Date.now()
  sessionData.expiresAt = now + SESSION_TIMEOUT_MS

  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData))
    return true
  } catch (error) {
    console.error('Failed to extend session:', error)
    return false
  }
}
